import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Clock3, Search } from 'lucide-react';

import { useTranslation } from '@sshop/i18n';
import { cn } from '@sshop/ui';

import { useProductFeed } from '@/features/catalog';
import { useLocale } from '@/shared/hooks/useLocale';
import { formatMoney } from '@/entities-ui/product';
import { routes } from '@/shared/lib/routes';

import { headerSearchStyles as s } from './HeaderSearch.styles';
import { clearSearchHistory, readSearchHistory, rememberSearch } from './searchHistory';

const DEBOUNCE_MS = 250;
const MIN_LEN = 2;
const PREVIEW_SIZE = 6;

export function HeaderSearch({ onFocusChange }: { onFocusChange?: (focused: boolean) => void }) {
  const { t } = useTranslation();
  const locale = useLocale();
  const navigate = useNavigate();
  const location = useLocation();
  const [value, setValue] = useState('');
  const [term, setTerm] = useState('');
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [history, setHistory] = useState(readSearchHistory);
  const [compactOpen, setCompactOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const closeSearch = useCallback(() => {
    setOpen(false);
    setActiveIndex(-1);
    setCompactOpen(false);
    inputRef.current?.blur();
    onFocusChange?.(false);
  }, [onFocusChange]);

  const resetSearch = useCallback(() => {
    setValue('');
    setTerm('');
    closeSearch();
  }, [closeSearch]);

  useEffect(() => {
    resetSearch();
  }, [location.pathname, location.search, resetSearch]);

  useEffect(() => {
    const id = window.setTimeout(() => setTerm(value.trim()), DEBOUNCE_MS);
    return () => window.clearTimeout(id);
  }, [value]);

  useEffect(() => {
    if (compactOpen) inputRef.current?.focus();
  }, [compactOpen]);

  useEffect(() => {
    const media = window.matchMedia('(max-width: 419px)');
    const keepFocusedInputVisible = () => {
      const inputFocused = document.activeElement === inputRef.current;
      setCompactOpen(media.matches && inputFocused);
      if (!inputFocused) {
        setOpen(false);
        onFocusChange?.(false);
      }
    };
    media.addEventListener('change', keepFocusedInputVisible);
    return () => media.removeEventListener('change', keepFocusedInputVisible);
  }, [onFocusChange]);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) closeSearch();
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [closeSearch]);

  const enabled = term.length >= MIN_LEN;
  const { data, isFetching } = useProductFeed(
    { search: term, page: 1, pageSize: PREVIEW_SIZE },
    enabled,
  );

  const valueMatchesQuery = value.trim() === term;
  const items = useMemo(
    () => (enabled && valueMatchesQuery ? (data?.items ?? []) : []),
    [enabled, valueMatchesQuery, data],
  );
  const historyOpen = open && value.trim().length === 0;
  const suggestionsOpen = open && value.trim().length >= MIN_LEN;
  const panelOpen = historyOpen || suggestionsOpen;
  const optionCount = historyOpen ? history.length : items.length;

  const recordSearch = (query: string) => {
    if (query.trim()) setHistory(rememberSearch(query));
  };

  const navigateToSearch = (query: string) => {
    const q = query.trim();
    recordSearch(q);
    resetSearch();
    navigate(q ? routes.search(locale, q) : routes.catalog(locale));
  };

  const submit = () => {
    navigateToSearch(value);
  };

  return (
    <div
      ref={rootRef}
      className={s.root}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
          onFocusChange?.(false);
          setOpen(false);
          if (window.matchMedia('(max-width: 419px)').matches) {
            setCompactOpen(false);
          }
        }
      }}
    >
      <button
        type="button"
        className={cn(s.iconButtonMobile, compactOpen && s.iconButtonMobileOpen)}
        aria-label={t('nav.search')}
        aria-expanded={compactOpen}
        onClick={() => {
          setCompactOpen(true);
          onFocusChange?.(true);
        }}
      >
        <Search className={s.iconGlyph} aria-hidden />
      </button>
      <form
        className={cn(s.form, compactOpen ? s.formCompactOpen : s.formCompactClosed)}
        role="search"
        onSubmit={(e) => {
          e.preventDefault();
          if (historyOpen && activeIndex >= 0 && history[activeIndex]) {
            navigateToSearch(history[activeIndex]);
          } else if (activeIndex >= 0 && items[activeIndex]) {
            recordSearch(value);
            resetSearch();
            navigate(routes.product(locale, items[activeIndex].slug));
          } else submit();
        }}
      >
        <div className={s.inputWrap}>
          <input
            ref={inputRef}
            type="search"
            inputMode="search"
            className={s.input}
            value={value}
            placeholder={t('nav.search')}
            aria-label={t('nav.search')}
            role="combobox"
            aria-autocomplete="list"
            aria-expanded={panelOpen}
            aria-controls={panelOpen ? 'header-search-suggestions' : undefined}
            aria-activedescendant={
              activeIndex >= 0
                ? `header-search-${historyOpen ? 'history' : 'option'}-${activeIndex}`
                : undefined
            }
            onChange={(e) => {
              setValue(e.target.value);
              setOpen(true);
              setActiveIndex(-1);
            }}
            onFocus={() => {
              setHistory(readSearchHistory());
              setOpen(true);
              onFocusChange?.(true);
            }}
            onKeyDown={(event) => {
              if (event.key === 'Escape') closeSearch();
              if (event.key === 'ArrowDown' && optionCount > 0) {
                event.preventDefault();
                setOpen(true);
                setActiveIndex((current) => (current + 1) % optionCount);
              }
              if (event.key === 'ArrowUp' && optionCount > 0) {
                event.preventDefault();
                setOpen(true);
                setActiveIndex((current) => (current <= 0 ? optionCount - 1 : current - 1));
              }
            }}
          />
          <span className={s.inputIcon} aria-hidden>
            <Search className={s.iconGlyph} />
          </span>
        </div>
      </form>

      {panelOpen && (
        <div className={s.panel} id="header-search-suggestions" role="listbox">
          {historyOpen ? (
            <>
              <div className={s.historyHeader}>
                <span>{t('search.recent')}</span>
                {history.length > 0 ? (
                  <button
                    type="button"
                    className={s.historyClear}
                    onClick={() => {
                      clearSearchHistory();
                      setHistory([]);
                      setActiveIndex(-1);
                      inputRef.current?.focus();
                    }}
                  >
                    {t('search.clearHistory')}
                  </button>
                ) : null}
              </div>
              {history.length === 0 ? (
                <p className={s.panelEmpty}>{t('search.noRecent')}</p>
              ) : (
                <ul className={s.list}>
                  {history.map((query, index) => (
                    <li
                      key={query}
                      id={`header-search-history-${index}`}
                      role="option"
                      aria-selected={index === activeIndex}
                    >
                      <button
                        type="button"
                        className={s.historyItem}
                        onMouseEnter={() => setActiveIndex(index)}
                        onClick={() => navigateToSearch(query)}
                      >
                        <Clock3 size={17} aria-hidden />
                        <span className={s.historyTerm}>{query}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </>
          ) : items.length === 0 ? (
            <p className={s.panelEmpty}>
              {isFetching || !valueMatchesQuery ? t('search.loading') : t('search.empty')}
            </p>
          ) : (
            <ul className={s.list}>
              {items.map((p, index) => (
                <li
                  key={p.id}
                  id={`header-search-option-${index}`}
                  role="option"
                  aria-selected={index === activeIndex}
                >
                  <Link
                    to={routes.product(locale, p.slug)}
                    className={s.item}
                    onMouseEnter={() => setActiveIndex(index)}
                    onClick={() => {
                      recordSearch(value);
                      resetSearch();
                    }}
                  >
                    {p.image ? (
                      <img src={p.image.url} alt={p.image.alt || p.title} className={s.thumb} />
                    ) : (
                      <span className={s.thumb} aria-hidden />
                    )}
                    <span className={s.itemBody}>
                      <span className={s.itemTitle}>{p.title}</span>
                      {p.brand && <span className={s.itemBrand}>{p.brand}</span>}
                      <span className={s.itemPrices}>
                        <span className={s.itemPrice}>{formatMoney(p.price, locale)}</span>
                        {p.compareAtPrice && p.compareAtPrice.amount > p.price.amount ? (
                          <span className={s.itemOriginalPrice}>
                            {formatMoney(p.compareAtPrice, locale)}
                          </span>
                        ) : null}
                      </span>
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
          {suggestionsOpen && value.trim().length > 0 && (
            <Link
              to={routes.search(locale, value.trim())}
              className={s.footer}
              onClick={() => {
                recordSearch(value);
                resetSearch();
              }}
            >
              <span>{t('search.viewAll')}</span>
              <span aria-hidden>→</span>
            </Link>
          )}
        </div>
      )}
    </div>
  );
}
